import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "../../lib/apiClient";
import { authHeader } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../ui/alert-dialog";
import { Sparkles, Send, Trash2, Eraser, BookmarkPlus, Check } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Suggest a calm, neutral color palette for this space.",
  "Recommend furniture styles that fit a classic aesthetic.",
  "Summarize the current status and next steps for the client.",
];

const isTempId = (id) => /^(u-|a-|stream)/.test(String(id));

export default function AIAssistantTab({ projectId, readOnly }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [savedIds, setSavedIds] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const endRef = useRef(null);

  const load = () => api.get(`/projects/${projectId}/ai-messages`).then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming || readOnly) return;
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: msg }, { id: "stream", role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/ai-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        credentials: "include",
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) {
            setMessages((m) => m.map((x) => x.id === "stream" ? { ...x, content: x.content + data.delta } : x));
          }
        }
      }
    } catch (e) {
      setMessages((m) => m.map((x) => x.id === "stream" ? { ...x, content: "Sorry, something went wrong." } : x));
    } finally {
      setStreaming(false);
      load();
    }
  };

  const deleteMessage = async (id) => {
    if (isTempId(id)) return;
    try {
      await api.delete(`/projects/${projectId}/ai-messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete message");
    }
  };

  const saveToNotes = async (m) => {
    if (isTempId(m.id) || savingId) return;
    setSavingId(m.id);
    try {
      await api.post(`/projects/${projectId}/messages`, {
        text: `From WorkRoom Assistant:\n\n${m.content}`,
        category: "Design",
        recipients: [],
      });
      setSavedIds((s) => [...s, m.id]);
      toast.success("Saved to Notes");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save to Notes");
    } finally {
      setSavingId(null);
    }
  };

  const clearAll = async () => {    setClearing(true);
    try {
      await api.delete(`/projects/${projectId}/ai-messages`);
      setMessages([]);
      toast.success("Assistant history cleared");
      setConfirmClear(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not clear history");
    } finally {
      setClearing(false);
    }
  };

  const canManage = !readOnly && messages.some((m) => !isTempId(m.id));

  return (
    <div className="max-w-3xl" data-testid="ai-tab">
      <div className="border border-[#EBE5E0] bg-white">
        <div className="flex items-center gap-3 border-b border-[#EBE5E0] p-5 bg-[#F9F8F6]">
          <div className="w-9 h-9 bg-[#A68A6B] text-white flex items-center justify-center">
            <Sparkles size={18} strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-serif text-lg text-[#2B2622] leading-tight">WorkRoom Assistant</p>
            <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">AI Design Partner</p>
          </div>
          {canManage && (
            <button data-testid="ai-clear-all-btn" onClick={() => setConfirmClear(true)}
              title="Clear all assistant history"
              className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#9b938b] hover:text-[#9E4747] transition-colors">
              <Eraser size={14} strokeWidth={1.5} /> Clear all
            </button>
          )}
        </div>

        <div className="h-[48vh] overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center mt-6">
              <p className="text-[#5C5651] text-sm mb-5">Ask for design ideas, palettes, or a project summary.</p>
              <div className="space-y-2 max-w-md mx-auto">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="ai-suggestion" onClick={() => send(s)}
                    className="block w-full text-left text-sm border border-[#EBE5E0] px-4 py-3 hover:border-[#A68A6B] hover:text-[#A68A6B] transition-colors text-[#5C5651]">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`group flex items-center gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid="ai-message">
              {m.role === "user" && !readOnly && !isTempId(m.id) && (
                <button data-testid={`ai-delete-${m.id}`} onClick={() => deleteMessage(m.id)}
                  title="Delete message"
                  className="text-[#C9BBAE] hover:text-[#9E4747] transition-colors opacity-0 group-hover:opacity-100 shrink-0">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              )}
              <div className={`max-w-[80%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#A68A6B] text-white" : "bg-[#F2EFEB] text-[#2B2622]"}`}>
                {m.content || <span className="text-[#9b938b]">…</span>}
              </div>
              {m.role === "assistant" && !readOnly && !isTempId(m.id) && (
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`ai-save-note-${m.id}`} onClick={() => saveToNotes(m)}
                    disabled={savingId === m.id || savedIds.includes(m.id)}
                    title={savedIds.includes(m.id) ? "Saved to Notes" : "Save to Notes"}
                    className="text-[#C9BBAE] hover:text-[#A68A6B] transition-colors disabled:cursor-default">
                    {savedIds.includes(m.id)
                      ? <Check size={14} strokeWidth={2} className="text-[#506B52]" />
                      : <BookmarkPlus size={14} strokeWidth={1.5} />}
                  </button>
                  <button data-testid={`ai-delete-${m.id}`} onClick={() => deleteMessage(m.id)}
                    title="Delete response"
                    className="text-[#C9BBAE] hover:text-[#9E4747] transition-colors">
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#EBE5E0] p-3 flex gap-2">
          <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={streaming || readOnly}
            placeholder={readOnly ? "Viewing only — chat is disabled" : "Ask the design assistant…"}
            className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          <Button data-testid="ai-send-btn" type="submit" disabled={streaming || readOnly}
            className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white px-5">
            <Send size={16} strokeWidth={1.5} />
          </Button>
        </form>
      </div>

      <AlertDialog open={confirmClear} onOpenChange={(o) => { if (!o) setConfirmClear(false); }}>
        <AlertDialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="ai-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#2B2622]">Clear assistant history?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5C5651]">
              This permanently deletes all previous assistant questions and responses for this project. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-clear-cancel" className="rounded-none border-[#EBE5E0] uppercase tracking-wider text-xs">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="ai-clear-confirm" onClick={(e) => { e.preventDefault(); clearAll(); }} disabled={clearing}
              className="rounded-none bg-[#9E4747] hover:bg-[#853a3a] text-white uppercase tracking-wider text-xs">
              {clearing ? "Clearing…" : "Clear All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
